import { useEffect, useState } from 'react';
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { data_fetch_api_resource_with_params } from "../../../utils/http_functions";
import { dayjs } from "../../../config";

import { regional_settings } from "../../../constants"

import { Grid, useMediaQuery, useTheme } from "@mui/material"

import { BarChart, AreaChart } from "../../Chart/v2";
import DateSelector from "../DateSelector";
import ChartSkeleton from "../../Chart/ChartSkeleton";
import { useSettingsContext } from '@/components/Settings/hooks/useSettingsContext';

const defaults = {
  type: 'monthly',
  startDate: dayjs().month(dayjs().month() - 12).date(1).startOf('day').hour(1),
  endDate: dayjs().date(1).startOf('day').startOf('hour')
};

const MONTH_MAP = {
  // Català
  'gen': 1, 'febr': 2, 'març': 3, 'abr': 4, 'maig': 5, 'juny': 6, 'jul': 7, 'ag': 8, 'set': 9, 'oct': 10, 'nov': 11, 'des': 12,
  // Castellà
  'ene': 1, 'feb': 2, 'mar': 3, 'abr': 4, 'may': 5, 'jun': 6, 'ago': 8, 'sep': 9, 'oct': 10, 'nov': 11, 'dic': 12,
  // Anglès
  'jan': 1, 'feb': 2, 'mar': 3, 'apr': 4, 'may': 5, 'jun': 6, 'jul': 7, 'aug': 8, 'sep': 9, 'oct': 10, 'nov': 11, 'dec': 12,
};

const parseChartName = (name, item) => {
  if (item?.date) return dayjs(item.date);
  if (!name || typeof name !== 'string') return null;

  // Format diari: "31'des.'23" o "31'des'23"
  const dailyMatch = name.match(/^(\d{1,2})'([a-zA-Zç]+)\.?'?(\d{2})$/);
  if (dailyMatch) {
    const [, day, monthStr, yearStr] = dailyMatch;
    const month = MONTH_MAP[monthStr.toLowerCase()];
    const year = 2000 + parseInt(yearStr);
    if (!month) return null;
    return dayjs(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
  }

  // Format mensual: "des.'23"
  const monthlyMatch = name.match(/^([a-zA-Zç]+)\.?'(\d{2})$/);
  if (monthlyMatch) {
    const [, monthStr, yearStr] = monthlyMatch;
    const month = MONTH_MAP[monthStr.toLowerCase()];
    const year = 2000 + parseInt(yearStr);
    if (!month) return null;
    return dayjs(`${year}-${String(month).padStart(2, '0')}-01`);
  }

  return null;
};

const formatChartName = (date, type) => {
  const monthLabel = date.format('MMM').toLowerCase();
  const year = date.format('YY');

  if (type === 'monthly') {
    return `${monthLabel}'${year}`;
  }
  if (type === 'daily') {
    return `${date.date()}'${monthLabel}'${year}`;
  }
  if (['hourly', 'quarter-hourly'].includes(type)) {
    return date.format('HH:mm');
  }
  return date.format();
};

const fillGaps = (data, dates) => {
  if (!data || !data.curve || !data.magnitudes?.elements) return data;

  const { type, startDate, endDate } = dates;
  if (type === 'daily') return data;

  const filledCurve = [];

  let current = dayjs(startDate);
  let end = dayjs(endDate);
  let stepUnit = 'day';
  let stepAmount = 1;

  if (type === 'monthly') {
    current = current.startOf('month');
    end = end.startOf('month');
    stepUnit = 'month';
  } else if (type === 'hourly') {
    current = current.startOf('hour');
    end = end.startOf('hour');
    stepUnit = 'hour';
  } else if (type === 'quarter-hourly') {
    current = current.startOf('hour').minute(Math.floor(current.minute() / 15) * 15);
    end = end.startOf('hour').minute(Math.floor(end.minute() / 15) * 15);
    stepUnit = 'minute';
    stepAmount = 15;
  }

  const existingDataMap = new Map();
  data.curve.forEach(item => {
    const parsedDate = parseChartName(item.name, item);
    if (parsedDate) {
      const key = parsedDate.startOf(stepUnit === 'minute' ? 'minute' : stepUnit).valueOf();
      existingDataMap.set(key, item);
    }
  });

  if (existingDataMap.size === 0 && data.curve.length > 0) return data;

  while (current.isBefore(end) || current.isSame(end, stepUnit)) {
    const key = current.valueOf();
    if (existingDataMap.has(key)) {
      filledCurve.push(existingDataMap.get(key));
    } else {
      const newItem = {
        name: formatChartName(current, type),
        total: 0
      };
      data.magnitudes.elements.forEach(el => {
        newItem[el] = 0;
      });
      filledCurve.push(newItem);
    }
    current = current.add(stepAmount, stepUnit);
  }

  return {
    ...data,
    curve: filledCurve
  };
};

const CCHChart = ({
  contractId,
  tariff,
  isPeninsula = true,
  chartComponents = {},
  setChartComponents
}) => {
  const Settings = useSettingsContext();
  const { t } = useTranslation();
  const token = useSelector((state) => state.auth.token);

  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState(null);
  const [dates, setDates] = useState({...defaults});
  const [displayDates, setDisplayDates] = useState({...defaults});

  const theme = useTheme();
  const breakpointDownMd = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchChartSpec();
  }, [dates, contractId]);

  const handleDatesChange = (newDates) => {
    setDates(newDates);
    setDisplayDates(newDates);
  }


  const handleBrushChange = (startName, endName) => {
    if (!chartData || !chartData.curve) return;
    if (!startName || !endName) return;

    const startDate = parseChartName(startName, chartData.curve.find(c => c.name === startName));
    let endDate = parseChartName(endName, chartData.curve.find(c => c.name === endName));

    if (!startDate || !endDate) return;

    if (dates.type === 'monthly') {
      endDate = endDate.endOf('month');
    }

    setDisplayDates(prev => ({
      ...prev,
      startDate,
      endDate,
    }));
  };

  const fetchChartSpec = () => {
    const translations = {
      'activa': t('common:text.contract_cch_active'),
      'exportada': t('common:text.contract_cch_exported'),
      'reactiva': t('common:text.contract_cch_reactive')
    }

    const spec_url = `contracts/${contractId}/cch/chart/${dates.type}/` +
      `?start=${dayjs(dates.startDate).utc(true).toISOString()}` +
      `&end=${dayjs(dates.endDate).utc(true).toISOString()}`;

    setLoading(true);
    data_fetch_api_resource_with_params(token, spec_url, { translations }, 2)
      .then((response) => {
        if (response.status === 200 && response.data) {
          const filledData = fillGaps(response.data, dates);
          setChartData(filledData);
        } else {
          setChartData(null);
        }
      }).finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    setChartComponents(getComponents());
  }, [chartData])

  const getComponents = () => {
    let components = {};

    let tariffText = '_domestic';
    let zonaText = '';

    if (tariff && !tariff.includes('2.0')) {
      tariffText = '_industrial';
      zonaText = "_peninsula";
      if (!isPeninsula) {
        zonaText = "_balears";
      }
    }

    chartData?.magnitudes?.elements.map((el, idx) => {
      components[el] = {
        title: el,
        label: t(`common:chartLegend.${el}${tariffText}${zonaText}`),
      }
    })
    return components
  }

  return (
    <>
      <DateSelector 
        onDatesChange={handleDatesChange} 
        contractId={contractId} 
        defaults={displayDates} 
        loading={loading} 
        endpointVersion={2}
      />

      <Grid container justifyContent="center" style={{ paddingTop: 20 }}>
        {loading &&
          <Grid item xs={12}>
            {dates.type === 'monthly' ? (
              <ChartSkeleton 
                type="columns" 
                columns={dayjs(dates.endDate).diff(dayjs(dates.startDate), 'month') + 1} 
              />
            ) : (
              <ChartSkeleton type="line" />
            )}
          </Grid>
        }
        {!loading &&
          <Grid item xs={12}>
            {chartData && <>
              {['daily', 'hourly', 'quarter-hourly'].includes(dates.type) &&
                <AreaChart
                  data={chartData}
                  unit={regional_settings.energy_unit}
                  colorsOffset={3}
                  showTooltipInfo={true}
                  onBrushChange={handleBrushChange}
                  xAxisHeight={dates.type === 'quarter-hourly' ? 130 : 120}
                  stacked={dates.type === 'daily'}
                />
              }
              {dates.type === 'monthly' &&
                <BarChart
                  data={chartData}
                  components={chartComponents}
                  unit={regional_settings.energy_unit}
                  enableLegendTooltip={!breakpointDownMd && Settings?.contract?.enableInfoCCH}
                  onBrushChange={handleBrushChange}
                />
              }
            </>}
            {!chartData &&
              <div style={{ textAlign: "center" }}>{t('common:text.contract_cch_chart_no_data')}</div>
            }
          </Grid>
        }
      </Grid>
    </>
  )
}

export default CCHChart;
